/** Tiny DOM helpers - just enough to avoid innerHTML string building. */

type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;

    if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'class') {
      node.className = String(value);
    } else if (key === 'text') {
      node.textContent = String(value);
    } else if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }

  for (const child of children) {
    if (child === null || child === undefined) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

/** Like `replaceChildren`, but skips null/undefined entries from conditional rendering. */
export function setChildren(
  node: HTMLElement,
  children: Array<Node | string | null | undefined>,
): void {
  node.replaceChildren(...children.filter((child): child is Node | string => child != null));
}

export function icon(symbol: string): HTMLSpanElement {
  return el('span', { class: 'icon', 'aria-hidden': 'true', text: symbol });
}

/** Page scaffold with an optional back button and action slot in the header. */
export function page(options: {
  title: string;
  back?: string;
  actions?: Array<Node>;
  children: Array<Node | null>;
}): HTMLElement {
  const header = el('header', { class: 'app-header' }, [
    options.back
      ? el('a', { class: 'btn btn-ghost btn-back', href: `#${options.back}`, 'aria-label': 'Zurück' }, [
          icon('‹'),
        ])
      : null,
    el('h1', { class: 'app-title', text: options.title }),
    el('div', { class: 'header-actions' }, options.actions ?? []),
  ]);

  return el('div', { class: 'page' }, [header, el('main', { class: 'app-main' }, options.children)]);
}

export function confirmAction(message: string): boolean {
  return window.confirm(message);
}
